import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Tabs,
  Tab,
  Grid,
  Paper,
  Drawer,
  Box,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogContent,
  TableSortLabel,
  Menu,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Cookies from "universal-cookie";

import IconComponent from "../../components/Icons/IconComponent";
import ExportComponent from "../../components/Exports/ExporterFiles";
import ExportIconButton from "../Generals/Buttons/ExportIconButton";
import RequestStatusPendingService from "../../Endpoints/RequestStatusPR";
import RequestStatusHistoryService from "../../Endpoints/RequestStatusHR";
import RequestStatusChangeService from "../../Endpoints/RequestStatusChange";
import { useCookies } from "../../Tools/CookieChecker";

import {
  PageRoot,
  MainCard__body,
  TitleWrap,
  Card,
  Toolbar,
  SearchBox,
  FiltersButton,
  HeaderCell,
  BodyCell,
  Footer,
  TotalText,
} from "./Styles.js";
import {
  buildTableRowsPerPageOptions,
  TABLE_BODY_ROW_HEIGHT,
  TABLE_COMPACT_HEADER_HEIGHT,
  TABLE_COMPACT_HEADER_PADDING,
  TABLE_HEADER_FONT_SIZE,
  TABLE_INITIAL_ROWS_PER_PAGE,
} from "../Table/constants.js";

const statusColor = {
  WAITING: "warning",
  NEED_REVIEW: "default",
  TAKEN: "primary",
  IN_PROGRESS: "info",
  REJECTED: "default",
  APPROVED: "success",
  RESOLVED: "success",
};

const HISTORY_PERIODS = [
  "Today",
  "Last 3 days",
  "Last week",
  "Last month",
  "Last 3 months",
  "Last 6 months",
  "Custom date",
];

const REQUEST_EXPORT_COLUMNS = [
  { field: "id", headerName: "Request ID", label: "Request ID" },
  { field: "date", headerName: "Submitted on", label: "Submitted on" },
  { field: "status", headerName: "Status", label: "Status" },
  { field: "type", headerName: "Request type", label: "Request type" },
  { field: "requester", headerName: "Requester", label: "Requester" },
  { field: "assignee", headerName: "Assignee", label: "Assignee" },
];

const exportButtonSx = {
  textTransform: "none",
  fontWeight: 800,
  fontSize: 15,
  height: "40px",
  minHeight: "40px",
  padding: "12px",
  boxShadow: "none",
  borderRadius: "8px",
  backgroundColor: "#EB0000",
  color: "#FFFFFF",
  borderColor: "#EB0000",
  "&:hover": {
    backgroundColor: "#D50000",
    borderColor: "#D50000",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#EB0000",
    color: "#FFFFFF",
    opacity: 0.5,
  },
};

const periodFieldSx = {
  minWidth: 220,
  "& .MuiOutlinedInput-root": { borderRadius: "10px", height: 40 },
  "& .MuiInputLabel-root": { fontSize: 13, top: -6 },
  "& .MuiInputLabel-root.Mui-focused": { color: "#15202A" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#15202A",
  },
};

function parseRequestDate(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const raw = String(value).trim();
  if (!raw || raw === "-") return null;

  const yearFirstMatch = raw.match(
    /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:[.:](\d{1,3}))?)?(?:\s*(Z|[+-]\d{2}:?\d{2}))?$/
  );

  if (yearFirstMatch) {
    const [, yyyy, mm, dd, hh = "00", mi = "00", ss = "00", ms = "0", tz = ""] = yearFirstMatch;
    if (tz) {
      const tzNormalized = tz === "Z" || tz.includes(":") ? tz : `${tz.slice(0, 3)}:${tz.slice(3)}`;
      const isoLike = `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}T${String(hh).padStart(2, "0")}:${String(mi).padStart(2, "0")}:${String(ss).padStart(2, "0")}.${String(ms).padEnd(3, "0")}${tzNormalized}`;
      const parsed = new Date(isoLike);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi), Number(ss), Number(String(ms).padEnd(3, "0"))));
  }

  const monthFirstMatch = raw.match(
    /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:[.:](\d{1,3}))?)?(?:\s*([AaPp][Mm]))?$/
  );

  if (monthFirstMatch) {
    const [, mm, dd, yyyy, rawHour = "00", mi = "00", ss = "00", ms = "0", meridiem = ""] = monthFirstMatch;
    let hh = Number(rawHour);
    const suffix = meridiem.toUpperCase();
    if (suffix === "PM" && hh < 12) hh += 12;
    if (suffix === "AM" && hh === 12) hh = 0;
    return new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd), hh, Number(mi), Number(ss), Number(String(ms).padEnd(3, "0"))));
  }

  const normalizedFallback = raw.replace(/\//g, "-").replace(" ", "T").replace(" +", "+").replace(" -", "-");
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedFallback);
  const fallback = new Date(hasTimezone ? normalizedFallback : `${normalizedFallback}Z`);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function compareRowsByMostRecent(a, b) {
  const dateA = parseRequestDate(a?.date);
  const dateB = parseRequestDate(b?.date);
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return dateB.getTime() - dateA.getTime();
}

function compareRowsBySubmittedOn(a, b, direction) {
  const mostRecentResult = compareRowsByMostRecent(a, b);
  return direction === "asc" ? -mostRecentResult : mostRecentResult;
}

function parseYYYYMMDD(s) {
  if (!s) return null;
  const [yyyy, mm, dd] = String(s).split("-").map((x) => parseInt(x, 10));
  if (!mm || !dd || !yyyy) return null;
  return new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0, 0));
}

function startOfDayUtc(d) {
  if (!d) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
}

function endOfDayUtc(d) {
  if (!d) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));
}

function subtractMonthsUtc(date, months) {
  const d = new Date(date);
  d.setUTCMonth(d.getUTCMonth() - months);
  return d;
}

function subtractDaysUtc(date, days) {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() - days);
  return d;
}

function getHistoryResolvedRange(period, fromDate, toDate) {
  const now = new Date();
  if (!period) return null;

  if (period === "Today" || period === "today") {
    return { from: startOfDayUtc(now), to: endOfDayUtc(now) };
  }
  if (period === "Last 3 days") {
    return { from: startOfDayUtc(subtractDaysUtc(now, 2)), to: endOfDayUtc(now) };
  }
  if (period === "Last week" || period === "week") {
    return { from: startOfDayUtc(subtractDaysUtc(now, 6)), to: endOfDayUtc(now) };
  }
  if (period === "Last month" || period === "month") {
    return { from: startOfDayUtc(subtractMonthsUtc(now, 1)), to: endOfDayUtc(now) };
  }
  if (period === "Last 3 months" || period === "3months") {
    return { from: startOfDayUtc(subtractMonthsUtc(now, 3)), to: endOfDayUtc(now) };
  }
  if (period === "Last 6 months" || period === "6months") {
    return { from: startOfDayUtc(subtractMonthsUtc(now, 6)), to: endOfDayUtc(now) };
  }
  if (period === "last24") {
    return { from: startOfDayUtc(subtractDaysUtc(now, 1)), to: endOfDayUtc(now) };
  }
  if (period === "Custom date" || period === "custom") {
    const from = startOfDayUtc(parseYYYYMMDD(fromDate));
    const to = endOfDayUtc(parseYYYYMMDD(toDate));
    if (!from || !to) return null;
    return { from, to };
  }
  return null;
}

function safeStr(v) {
  return (v ?? "").toString().toLowerCase();
}

function rowMatchesQuery(row, q) {
  if (!q) return true;
  const query = q.toLowerCase().trim();
  if (!query) return true;
  const haystack = [row.id, row.date, row.status, row.type, row.requester, row.assignee]
    .map(safeStr)
    .join(" | ");
  return haystack.includes(query);
}

function statusChipSx(status) {
  if (status === "WAITING") return { color: "#000000", backgroundColor: "#FEEFC7" };
  if (status === "TAKEN") return { color: "#000000", backgroundColor: "#D1E0FF" };
  if (status === "NEED_REVIEW") return { color: "#000000", backgroundColor: "#E5E5E5" };
  if (status === "REJECTED") return { color: "#EB0000", backgroundColor: "#FDECEC" };
  if (status === "APPROVED" || status === "RESOLVED") {
    return { color: "#000000", backgroundColor: "#E6F5EA" };
  }
  return undefined;
}

function EmptyHistoryState() {
  return (
    <Box sx={{ py: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box sx={{ textAlign: "center", maxWidth: 520 }}>
        <Typography sx={{ color: "text.secondary", fontSize: 16, lineHeight: 1.5 }}>
          Sorry, we didn't find any requests under the filters you entered. Please try a different one.
        </Typography>
      </Box>
    </Box>
  );
}

function EmptyPendingState() {
  return (
    <Box sx={{ py: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: "text.secondary", fontSize: 16, lineHeight: 1.5 }}>
        Sorry, we didn't find any pending requests. Please try a different one.
      </Typography>
    </Box>
  );
}

function LoadingState({ text }) {
  return (
    <Box sx={{ py: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress size={34} />
        <Typography sx={{ mt: 2, color: "text.secondary", fontSize: 14 }}>
          {text || "Loading..."}
        </Typography>
      </Box>
    </Box>
  );
}

const fieldLabelSx = { fontWeight: 700, color: "text.secondary" };

function Field({ label, value }) {
  return (
    <>
      <Typography variant="caption" sx={fieldLabelSx}>{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{value || "-"}</Typography>
    </>
  );
}

function formatRequestFieldLabel(key) {
  const normalized = String(key ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!normalized) return "-";
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}:`;
}

function formatDecimalValue(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : value;
}

function normalizeResponse(apiData) {
  const list = Array.isArray(apiData?.requests) ? apiData.requests : [];
  return list.map((x) => {
    const id = x.request_id ?? x.id ?? x.uuid ?? x._id ?? "-";
    const date = x.date ?? "-";
    const statusRaw = (x.status ?? "-").toString().trim();
    const status = statusRaw.toUpperCase().replace(/\s+/g, "_");
    const type = (x.type ?? "-").toString().trim();
    const requesterEmail = x.requester?.email ?? x.requesterEmail ?? x.email ?? x.requestedBy ?? "-";
    const requesterName = x.requester?.name ?? "";
    const requester = requesterName ? `${requesterName} (${requesterEmail})` : requesterEmail;
    const assigneeEmail = x.assignee?.email ?? "";
    const assigneeName = x.assignee?.name ?? "";
    const assignee = assigneeName
      ? `${assigneeName} (${assigneeEmail || "—"})`
      : assigneeEmail || "Not assigned";
    return {
      id,
      date,
      status,
      type,
      requester,
      assignee,
      details: {
        submittedAt: x.date ?? "-",
        requestType: type,
        info: x.details ?? "",
        transaction: x.request ?? {},
      },
    };
  });
}

function buildPanels(apiData) {
  const wallet = apiData?.tvHouse?.wallet;
  const walletValue =
    wallet && typeof wallet.balance === "number"
      ? `${formatDecimalValue(wallet.balance)} ${wallet.currency || ""}`.trim()
      : "-";
  return [
    { label: "Waiting", value: apiData?.waiting ?? "-", icon: "error" },
    { label: "In progress", value: apiData?.inProgress ?? "-", icon: "search_activity" },
    { label: "Rejected", value: apiData?.rejected ?? "-", icon: "cancel" },
    { label: "Resolved", value: apiData?.resolved ?? "-", icon: "check_circle" },
    { label: "TV-House wallet", value: walletValue, icon: "account_balance_wallet" },
  ];
}

function buildPageItems(page, totalPages) {
  const last = totalPages;
  const current = page + 1;
  if (last <= 4) return Array.from({ length: last }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3, 4, "…"];
  if (current >= last - 1) return ["…", last - 3, last - 2, last - 1, last];
  return ["…", current - 1, current, current + 1, "…"];
}

const arrowBtnSx = (disabled) => ({
  minWidth: 30,
  height: 30,
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  border: "1px solid #E5E7EB",
  color: disabled ? "#9CA3AF" : "#374151",
  boxShadow: "none",
  padding: 0,
  "&:hover": { backgroundColor: "#F9FAFB", borderColor: "#D1D5DB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#FFFFFF", borderColor: "#E5E7EB", color: "#9CA3AF", opacity: 1 },
});

const selectedRowSx = { backgroundColor: "#ECF1F7" };

const requestHeaderCellSx = {
  fontSize: TABLE_HEADER_FONT_SIZE,
  fontWeight: 700,
  color: "rgba(0,0,0,0.70)",
  backgroundColor: "#F9F9F9",
  borderBottom: 0,
  height: TABLE_COMPACT_HEADER_HEIGHT,
  padding: TABLE_COMPACT_HEADER_PADDING,
  lineHeight: "18px",
  position: "relative",
  userSelect: "none",
};

const columnSeparatorSx = {
  position: "absolute",
  top: 0,
  right: 0,
  width: "12px",
  height: "100%",
  cursor: "col-resize",
  touchAction: "none",
  "&::after": {
    content: '""',
    position: "absolute",
    top: "22%",
    bottom: "22%",
    left: "50%",
    width: "2px",
    transform: "translateX(-50%)",
    borderRadius: "2px",
    backgroundColor: "rgba(21, 32, 42, 0.22)",
  },
  "&:hover::after, &:focus-visible::after": {
    backgroundColor: "#15202A",
  },
  "&:focus-visible": {
    outline: "2px solid #15202A",
    outlineOffset: "-2px",
  },
};

const requestStatusColumns = [
  { key: "id", label: "Request ID", minWidth: 130, width: 150 },
  { key: "date", label: "Submitted on", minWidth: 160, width: 180 },
  { key: "status", label: "Status", minWidth: 130, width: 150 },
  { key: "type", label: "Request type", minWidth: 170, width: 200 },
  { key: "requester", label: "Requester", minWidth: 220, width: 260 },
  { key: "assignee", label: "Assignee", minWidth: 190, width: 220 },
];

const initialColumnWidths = requestStatusColumns.reduce((widths, column) => {
  widths[column.key] = column.width;
  return widths;
}, {});

export default function RequestStatus() {
  const { user: sessionEmail } = useCookies() || {};
  const ROWS_OPTIONS = buildTableRowsPerPageOptions([5, 10, 25, 50, 100]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_INITIAL_ROWS_PER_PAGE);
  const [tab, setTab] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [handleLoading, setHandleLoading] = useState(false);
  const [pendingSearch, setPendingSearch] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [period, setPeriod] = useState("Today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedPeriod, setAppliedPeriod] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");
  const [pendingApiRows, setPendingApiRows] = useState([]);
  const [pendingPanels, setPendingPanels] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState("");
  const [historyApiRows, setHistoryApiRows] = useState([]);
  const [historyPanels, setHistoryPanels] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyApplied, setHistoryApplied] = useState(false);
  const [columnWidths, setColumnWidths] = useState(initialColumnWidths);
  const [submittedOnSortDirection, setSubmittedOnSortDirection] = useState("desc");
  const [statusMessageModalOpen, setStatusMessageModalOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusMessageIsError, setStatusMessageIsError] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);

  const tableMinWidth = useMemo(
    () => requestStatusColumns.reduce((total, column) => total + (columnWidths[column.key] || column.width), 0),
    [columnWidths]
  );

  const columnCellSx = useMemo(() => {
    return requestStatusColumns.reduce((styles, column) => {
      const width = columnWidths[column.key] || column.width;
      styles[column.key] = {
        width,
        minWidth: column.minWidth,
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
      };
      return styles;
    }, {});
  }, [columnWidths]);

  const handleColumnResizeStart = useCallback((event, column) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = columnWidths[column.key] || column.width;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const handlePointerMove = (moveEvent) => {
      const nextWidth = Math.max(column.minWidth, startWidth + moveEvent.clientX - startX);
      setColumnWidths((prev) => ({ ...prev, [column.key]: nextWidth }));
    };
    const handlePointerUp = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  }, [columnWidths]);

  const handleColumnResizeKeyDown = useCallback((event, column) => {
    const direction = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!direction) return;
    event.preventDefault();
    event.stopPropagation();
    setColumnWidths((prev) => ({
      ...prev,
      [column.key]: Math.max(column.minWidth, (prev[column.key] || column.width) + direction * 16),
    }));
  }, []);

  const handleRowClick = (row) => {
    setSelectedRequest(row);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => setDrawerOpen(false);

  const updateSelectedRequestStatus = async (nextStatus, payloadStatus = nextStatus) => {
    if (!selectedRequest) return;
    const cookies = new Cookies();
    const assigneeEmail = String(cookies.get("user") || sessionEmail || "").trim().toLowerCase();
    if (!assigneeEmail) {
      setPendingError("Session email is not available");
      setHistoryError("Session email is not available");
      return;
    }
    setHandleLoading(true);
    try {
      const response = await RequestStatusChangeService.updateRequestStatus({
        request_id: selectedRequest.id,
        status: payloadStatus,
        assignee: { email: assigneeEmail },
      });
      const backendMessage = response?.data?.message ?? response?.message ?? "Request status updated successfully";
      setStatusMessage(String(backendMessage));
      setStatusMessageIsError(false);
      setStatusMessageModalOpen(true);
      const formatAssignee = (email) => email || "Not assigned";
      setPendingApiRows((prev) => prev.map((row) => row.id === selectedRequest.id ? { ...row, status: nextStatus, assignee: formatAssignee(assigneeEmail) } : row));
      setHistoryApiRows((prev) => prev.map((row) => row.id === selectedRequest.id ? { ...row, status: nextStatus, assignee: formatAssignee(assigneeEmail) } : row));
      setSelectedRequest((prev) => prev ? { ...prev, status: nextStatus, assignee: formatAssignee(assigneeEmail) } : prev);
    } catch (error) {
      const backendMessage = error?.response?.data?.message ?? error?.message ?? "Failed to update request status";
      setStatusMessage(String(backendMessage));
      setStatusMessageIsError(true);
      setStatusMessageModalOpen(true);
    } finally {
      setHandleLoading(false);
    }
  };

  const handleTakeRequest = async () => {
    if (selectedRequest?.status !== "WAITING") return;
    await updateSelectedRequestStatus("TAKEN");
  };
  const handleRejectRequest = async () => {
    if (selectedRequest?.status !== "TAKEN") return;
    await updateSelectedRequestStatus("REJECTED");
  };
  const handleApproveRequest = async () => {
    if (selectedRequest?.status !== "TAKEN") return;
    await updateSelectedRequestStatus("APPROVED", "APPROVED");
  };

  useEffect(() => {
    if (tab !== 0) return;
    let alive = true;
    (async () => {
      setPendingLoading(true);
      setPendingError("");
      try {
        const res = await RequestStatusPendingService.getRequestStatusPending();
        if (!res) {
          if (!alive) return;
          setPendingError("Failed to load pending requests");
          setPendingApiRows([]);
          return;
        }
        const apiData = res?.data?.data ?? res?.data;
        if (!alive) return;
        setPendingApiRows(normalizeResponse(apiData));
        setPendingPanels(buildPanels(apiData));
      } catch (e) {
        if (!alive) return;
        setPendingError(e?.message || "Failed to load pending requests");
        setPendingApiRows([]);
      } finally {
        if (alive) setPendingLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tab]);

  const loadHistory = async (nextPeriod, nextFrom, nextTo) => {
    const range = getHistoryResolvedRange(nextPeriod, nextFrom, nextTo);
    if (!range?.from || !range?.to) return;

    setHistoryLoading(true);
    setHistoryError("");
    try {
      const res = await RequestStatusHistoryService.getRequestStatusHistory({
        from: range.from,
        to: range.to,
      });
      if (!res) {
        setHistoryError("Failed to load history requests");
        return;
      }
      const apiData = res?.data?.data ?? res?.data;
      setAppliedPeriod(nextPeriod);
      setAppliedFromDate(nextFrom);
      setAppliedToDate(nextTo);
      setHistoryApplied(true);
      setHistoryApiRows(normalizeResponse(apiData));
      setHistoryPanels(buildPanels(apiData));
      setPage(0);
      setDrawerOpen(false);
      setSelectedRequest(null);
    } catch (e) {
      setHistoryError(e?.message || "Failed to load history requests");
    } finally {
      setHistoryLoading(false);
    }
  };

  const draftResolvedRange = useMemo(() => {
    if (tab !== 1) return null;
    return getHistoryResolvedRange(period, fromDate, toDate);
  }, [tab, period, fromDate, toDate]);

  const appliedResolvedRange = useMemo(() => {
    if (tab !== 1) return null;
    return getHistoryResolvedRange(appliedPeriod, appliedFromDate, appliedToDate);
  }, [tab, appliedPeriod, appliedFromDate, appliedToDate]);

  const canApplyHistory =
    Boolean(period) &&
    (period !== "Custom date" || (fromDate && toDate)) &&
    Boolean(draftResolvedRange?.from && draftResolvedRange?.to);

  const handleApplyHistory = () => loadHistory(period, fromDate, toDate);

  const pendingData = useMemo(() => pendingApiRows, [pendingApiRows]);
  const historyData = useMemo(() => historyApiRows, [historyApiRows]);

  const rows = useMemo(() => {
    const base = tab === 0 ? pendingData : historyData;
    if (tab === 0) {
      return base
        .filter((r) => rowMatchesQuery(r, pendingSearch))
        .sort((a, b) => compareRowsBySubmittedOn(a, b, submittedOnSortDirection));
    }
    let filtered = [...base];
    if (searchEmail.trim()) filtered = filtered.filter((r) => rowMatchesQuery(r, searchEmail));
    if (appliedResolvedRange?.from && appliedResolvedRange?.to) {
      filtered = filtered.filter((r) => {
        const d = parseRequestDate(r.date);
        if (!d) return false;
        return d >= appliedResolvedRange.from && d <= appliedResolvedRange.to;
      });
    }
    return filtered.sort((a, b) => compareRowsBySubmittedOn(a, b, submittedOnSortDirection));
  }, [tab, pendingData, historyData, pendingSearch, searchEmail, appliedResolvedRange, submittedOnSortDirection]);

  const handleSubmittedOnSort = () => {
    setSubmittedOnSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [tab, pendingSearch, searchEmail, appliedPeriod, appliedFromDate, appliedToDate]);

  const totalCount = rows.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / rowsPerPage)), [totalCount, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const showEmptyPending = tab === 0 && !pendingLoading && !pendingError && rows.length === 0;
  const showEmptyHistory = tab === 1 && historyApplied && !historyLoading && !historyError && rows.length === 0;
  const panelsToRender = tab === 0 ? pendingPanels : historyPanels.length ? historyPanels : pendingPanels;
  const isPendingTab = tab === 0;
  const isHistoryTab = tab === 1;
  const isLoading = (isPendingTab && pendingLoading) || (isHistoryTab && historyLoading);
  const errorMsg = isPendingTab ? pendingError : historyError;
  const loadingText = isPendingTab ? "Loading pending requests..." : "Loading history requests...";

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);
  const handleExport = (type) => {
    if (!rows.length) {
      handleExportClose();
      return;
    }
    const formattedDate = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const fileName = `request_status_history_${formattedDate}`;
    if (type === "csv") ExportComponent.GenerateCSV(REQUEST_EXPORT_COLUMNS, rows, fileName);
    if (type === "pdf") ExportComponent.GeneratePDF(REQUEST_EXPORT_COLUMNS, rows, fileName);
    if (type === "txt") ExportComponent.GenerateTXT(REQUEST_EXPORT_COLUMNS, rows, fileName);
    handleExportClose();
  };

  const openHistoryWithToday = () => {
    setPeriod("Today");
    setFromDate("");
    setToDate("");
    loadHistory("Today", "", "");
  };

  return (
    <PageRoot>
      <TitleWrap sx={{ mb: 4 }}>
        <Typography sx={{ fontFamily: "Plus Jakarta Sans", fontSize: "26px", fontWeight: 600, lineHeight: "140%" }}>
          Request status
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Check here the pending, ongoing or resolved requests from Brokers and IBs.
        </Typography>
      </TitleWrap>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {(panelsToRender.length ? panelsToRender : [
          { label: "Waiting", value: "-", icon: "error" },
          { label: "In progress", value: "-", icon: "search_activity" },
          { label: "Rejected", value: "-", icon: "cancel" },
          { label: "Resolved", value: "-", icon: "check_circle" },
          { label: "TV-House wallet", value: "-", icon: "account_balance_wallet" },
        ]).map((item) => (
          <Grid item xs={12} sm={6} md={2.4} key={item.label}>
            <Paper elevation={0} sx={{ padding: "30px", borderRadius: "16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 3, background: "transparent", boxShadow: "0 10px 30px rgba(0,0,0,0.06)" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0, flex: 1 }}>
                <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                <Typography variant="h6">{item.value}</Typography>
              </Box>
              <Box sx={{ color: "#3F4D5E", alignSelf: "center" }}>
                <IconComponent icon={item.icon} />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <MainCard__body>
        <Card>
          <Box sx={{ mt: "-10px", mb: 1, borderRadius: "12px", border: "1px solid #ECF1F7", background: "#FFF" }}>
            <Tabs
              value={tab}
              onChange={(_, v) => {
                setTab(v);
                setPage(0);
                setDrawerOpen(false);
                setSelectedRequest(null);
                if (v === 1) openHistoryWithToday();
              }}
              TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
              sx={{
                pt: 0.5,
                "& .MuiTabs-indicator": { display: "flex", justifyContent: "center", backgroundColor: "transparent", height: 4 },
                "& .MuiTabs-indicatorSpan": { width: "30%", borderRadius: "2px", backgroundColor: "#15202A" },
              }}
            >
              <Tab label="Pending" />
              <Tab label="History" />
            </Tabs>
          </Box>

          {tab === 0 ? (
            <Toolbar>
              <SearchBox>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search request"
                  value={pendingSearch}
                  onChange={(e) => { setPendingSearch(e.target.value); setPage(0); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", height: 40 } }}
                />
              </SearchBox>
            </Toolbar>
          ) : (
            <Toolbar sx={{ gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
              <SearchBox sx={{ maxWidth: 320 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search request"
                  value={searchEmail}
                  onChange={(e) => { setSearchEmail(e.target.value); setPage(0); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", height: 40 } }}
                />
              </SearchBox>

              <TextField
                select
                size="small"
                label="Period of time"
                value={period}
                onChange={(e) => {
                  const nextPeriod = e.target.value;
                  setPeriod(nextPeriod);
                  setPage(0);
                  setHistoryError("");
                  if (nextPeriod !== "Custom date") {
                    setFromDate("");
                    setToDate("");
                  }
                }}
                sx={periodFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                {HISTORY_PERIODS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>

              {period === "Custom date" && (
                <>
                  <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFromDate(v);
                      setPage(0);
                      if (toDate && v && toDate < v) setToDate("");
                      setHistoryError("");
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 170, ...periodFieldSx }}
                  />
                  <TextField
                    size="small"
                    label="To"
                    type="date"
                    value={toDate}
                    onChange={(e) => { setToDate(e.target.value); setPage(0); setHistoryError(""); }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: fromDate || undefined }}
                    sx={{ minWidth: 170, ...periodFieldSx }}
                  />
                </>
              )}

              <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto" }}>
                <FiltersButton
                  variant="text"
                  onClick={() => {
                    setSearchEmail("");
                    setPeriod("Today");
                    setFromDate("");
                    setToDate("");
                    setHistoryError("");
                    openHistoryWithToday();
                  }}
                  startIcon={<IconComponent name="refresh" />}
                  sx={{ height: "40px", padding: "12px" }}
                >
                  Clear
                </FiltersButton>

                <ExportIconButton
                  disabled={isLoading || !rows.length}
                  onClick={handleExportClick}
                  sx={exportButtonSx}
                >
                  <IconComponent icon="download" size="16px" />
                  Export
                </ExportIconButton>
                <Menu
                  anchorEl={exportAnchorEl}
                  open={exportOpen}
                  onClose={handleExportClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem onClick={() => handleExport("csv")}>Export as CSV</MenuItem>
                  <MenuItem onClick={() => handleExport("pdf")}>Export as PDF</MenuItem>
                  <MenuItem onClick={() => handleExport("txt")}>Export as TXT</MenuItem>
                </Menu>

                <FiltersButton
                  disabled={!canApplyHistory || historyLoading}
                  onClick={handleApplyHistory}
                  sx={{
                    height: "40px",
                    padding: "12px",
                    backgroundColor: "#EB0000",
                    color: "#FFFFFF",
                    borderRadius: "10px",
                    "&:hover": { backgroundColor: "#D50000" },
                    "&.Mui-disabled": { backgroundColor: "#FCA5A5", color: "#FFFFFF" },
                  }}
                >
                  Apply
                </FiltersButton>
              </Box>
            </Toolbar>
          )}

          <Divider />

          {isLoading ? (
            <LoadingState text={loadingText} />
          ) : errorMsg ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: "#EB0000" }}>{errorMsg}</Typography>
            </Box>
          ) : showEmptyPending ? (
            <EmptyPendingState />
          ) : showEmptyHistory ? (
            <EmptyHistoryState />
          ) : (
            <TableContainer sx={{ width: "100%", borderRadius: "12px", border: "1px solid rgba(0,0,0,0.06)", overflowX: "auto", backgroundColor: "#FFFFFF" }}>
              <Table
                sx={{
                  minWidth: tableMinWidth,
                  tableLayout: "fixed",
                  backgroundColor: "#FFFFFF",
                  "& .MuiTableCell-root:first-of-type": { pl: "20px" },
                }}
              >
                <colgroup>
                  {requestStatusColumns.map((column) => (
                    <col key={column.key} style={{ width: `${columnWidths[column.key] || column.width}px` }} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    {requestStatusColumns.map((column) => (
                      <HeaderCell
                        key={column.key}
                        sx={{
                          ...columnCellSx[column.key],
                          ...requestHeaderCellSx,
                          pr: TABLE_COMPACT_HEADER_PADDING,
                        }}
                      >
                        <Box
                          component="span"
                          title={column.label}
                          sx={{
                            display: "block",
                            lineHeight: "18px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {column.key === "date" ? (
                            <TableSortLabel
                              active
                              direction={submittedOnSortDirection}
                              onClick={handleSubmittedOnSort}
                              sx={{
                                color: "inherit",
                                fontWeight: "inherit",
                                lineHeight: "18px",
                                "&.Mui-active": { color: "inherit" },
                                "& .MuiTableSortLabel-icon": {
                                  color: "inherit !important",
                                  fontSize: 16,
                                },
                              }}
                            >
                              {column.label}
                            </TableSortLabel>
                          ) : (
                            column.label
                          )}
                        </Box>
                        <Box
                          component="span"
                          role="separator"
                          aria-label={`Resize ${column.label} column`}
                          aria-orientation="vertical"
                          aria-valuemin={column.minWidth}
                          aria-valuenow={columnWidths[column.key] || column.width}
                          tabIndex={0}
                          onPointerDown={(event) => handleColumnResizeStart(event, column)}
                          onKeyDown={(event) => handleColumnResizeKeyDown(event, column)}
                          sx={columnSeparatorSx}
                        />
                      </HeaderCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleRowClick(row)}
                      sx={{ cursor: "pointer", height: TABLE_BODY_ROW_HEIGHT, ...(selectedRequest?.id === row.id ? selectedRowSx : {}) }}
                    >
                      <BodyCell sx={columnCellSx.id}>{row.id}</BodyCell>
                      <BodyCell sx={columnCellSx.date}>{row.date}</BodyCell>
                      <BodyCell sx={columnCellSx.status}>
                        <Chip label={row.status} size="small" color={statusColor[row.status] || "default"} sx={statusChipSx(row.status)} />
                      </BodyCell>
                      <BodyCell sx={columnCellSx.type}>{row.type}</BodyCell>
                      <BodyCell sx={columnCellSx.requester}>{row.requester}</BodyCell>
                      <BodyCell sx={columnCellSx.assignee}>{row.assignee}</BodyCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Footer>
            <TotalText>Total: {totalCount}</TotalText>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                Pages:&nbsp;<b style={{ color: "#111827" }}>{totalPages}</b>
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>
              <Select
                size="small"
                value={rowsPerPage}
                disabled={isLoading}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                sx={{ height: 32, minWidth: 70, borderRadius: 2 }}
              >
                {ROWS_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Button size="small" disabled={isLoading || page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} sx={arrowBtnSx(isLoading || page === 0)}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                </Button>
                {pageItems.map((it, idx) => it === "…" ? (
                  <Typography key={`dots-${idx}`} sx={{ fontSize: 14, color: "#9CA3AF", px: 0.5 }}>…</Typography>
                ) : (
                  <Button key={`p-${it}`} size="small" disabled={isLoading} onClick={() => setPage(it - 1)} sx={{ minWidth: 28, height: 28, borderRadius: 2, fontWeight: it === page + 1 ? 900 : 700, color: it === page + 1 ? "#111827" : "#6B7280", backgroundColor: it === page + 1 ? "#EEF2FF" : "transparent" }}>
                    {it}
                  </Button>
                ))}
                <Button size="small" disabled={isLoading || page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} sx={arrowBtnSx(isLoading || page >= totalPages - 1)}>
                  <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                </Button>
              </Box>
            </Box>
          </Footer>
        </Card>
      </MainCard__body>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 520 },
            maxHeight: "calc(100vh - 60px)",
            borderRadius: "10px",
            margin: "30px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ p: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Request manager</Typography>
          <IconButton onClick={handleCloseDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        {!selectedRequest ? (
          <Box sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Select a request to view details.
            </Typography>
          </Box>
        ) : (
          (() => {
            const d = selectedRequest?.details;
            const t = d?.transaction;
            const transactionFields = Object.entries(t ?? {}).filter(([, value]) => {
              if (value === undefined || value === null) return false;
              if (typeof value === "string") return value.trim() !== "";
              return true;
            });
            const labelSx = { fontWeight: 700, color: "text.secondary" };
            const labelBlockSx = { ...labelSx, mt: 1.25, display: "block" };
            const actionButtonSx = {
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            };

            return (
              <>
                <Box sx={{ p: 2.5 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, background: "#ECF1F7" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={labelSx}>Request ID:</Typography>
                        <Typography variant="body2">{selectedRequest?.id || "-"}</Typography>
                        <Typography variant="caption" sx={labelBlockSx}>Submitted on:</Typography>
                        <Typography variant="body2">{d?.submittedAt || "-"}</Typography>
                        <Typography variant="caption" sx={labelBlockSx}>Requested by:</Typography>
                        <Typography variant="body2">{selectedRequest?.requester || "-"}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={labelSx}>Status:</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={selectedRequest?.status || "-"}
                            size="small"
                            color={statusColor[selectedRequest?.status] || "default"}
                            sx={statusChipSx(selectedRequest?.status)}
                          />
                        </Box>
                        <Typography variant="caption" sx={labelBlockSx}>Request type:</Typography>
                        <Typography variant="body2">{d?.requestType || "-"}</Typography>
                        <Typography variant="caption" sx={labelBlockSx}>Details:</Typography>
                        <Typography variant="body2">{d?.info || "-"}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
                <Divider />
                <Box sx={{ pl: "35px", pr: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Transaction details</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {selectedRequest?.status === "WAITING" && (
                      <Button variant="contained" onClick={handleTakeRequest} disabled={handleLoading} sx={{ ...actionButtonSx, backgroundColor: "#EB0000", "&:hover": { backgroundColor: "#D50000", boxShadow: "none" } }}>
                        {handleLoading ? "Handling..." : "Handle"}
                      </Button>
                    )}
                    {selectedRequest?.status === "TAKEN" && (
                      <>
                        <Button variant="outlined" onClick={handleRejectRequest} disabled={handleLoading} sx={{ ...actionButtonSx, border: "1px solid #15202A", color: "#15202A" }}>
                          Reject
                        </Button>
                        <Button variant="contained" onClick={handleApproveRequest} disabled={handleLoading} sx={{ ...actionButtonSx, backgroundColor: "#EB0000", "&:hover": { backgroundColor: "#D50000", boxShadow: "none" } }}>
                          Approve
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
                <Box sx={{ pl: "35px", pr: 2.5, pb: 2, flex: 1, overflowY: "auto" }}>
                  <Grid container spacing={2}>
                    {transactionFields.length > 0 ? (
                      transactionFields.map(([key, value]) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <Field label={formatRequestFieldLabel(key)} value={typeof value === "object" ? JSON.stringify(value) : String(value)} />
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Field label="Request:" value="-" />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            );
          })()
        )}
      </Drawer>

      <Dialog open={statusMessageModalOpen} onClose={() => setStatusMessageModalOpen(false)}>
        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          {statusMessageIsError ? <ErrorOutlineIcon sx={{ fontSize: 54, color: "#DC2626" }} /> : <CheckCircleIcon sx={{ fontSize: 54, color: "#16A34A" }} />}
          <Typography sx={{ fontSize: 22, fontWeight: 900 }}>{statusMessage}</Typography>
          <Button variant="contained" color="error" onClick={() => setStatusMessageModalOpen(false)} sx={{ mt: 2, textTransform: "none" }}>Close</Button>
        </DialogContent>
      </Dialog>
    </PageRoot>
  );
}